import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, History, PackagePlus, ShoppingBag, ShoppingCart } from "lucide-react";

import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../../components/ui/alert-dialog";
import { comprasDoIngrediente, resumo } from "../lib/historico";
import { ROTA_COMPRAS } from "../rotas";
import useNovaCompra from "../useNovaCompra";
import CabecalhoCompra from "./CabecalhoCompra";
import HistoricoIngrediente from "./HistoricoIngrediente";
import ItensDaCompra from "./ItensDaCompra";
import RodapeCompra from "./RodapeCompra";
import LancarItem from "./LancarItem";
import NovaCompraCarregando from "./NovaCompraCarregando";
import useLancamento from "./useLancamento";

const ABAS = [
  { id: "lancar", rotulo: "Adicionar", icone: PackagePlus },
  { id: "historico", rotulo: "Histórico", icone: History },
  { id: "itens", rotulo: "Itens", icone: ShoppingBag },
];

// Estoque → Compras → Lançar compra (tela inteira). Também edita uma compra existente.
export default function NovaCompraPage() {
  const { loteId } = useParams();
  const navigate = useNavigate();
  const c = useNovaCompra(loteId);
  useEffect(() => { if (c.naoEncontrada) navigate(ROTA_COMPRAS, { replace: true }); }, [c.naoEncontrada, navigate]);
  const l = useLancamento(c.porId);
  const [verIng, setVerIng] = useState("");
  const [editando, setEditando] = useState(null);
  const [aba, setAba] = useState("lancar");
  const [sair, setSair] = useState(null); // "zerar" (fica na tela) | "sair"

  const historicoDe = useMemo(() => {
    const cache = new Map();
    return (id) => {
      if (!cache.has(id)) {
        const compras = comprasDoIngrediente(c.lotes, id, { ignorarLote: loteId });
        cache.set(id, { compras, resumo: resumo(compras) });
      }
      return cache.get(id);
    };
  }, [c.lotes, loteId]);

  const ingHistorico = c.porId.get(l.ingId || verIng) || null;
  const hist = ingHistorico ? historicoDe(ingHistorico.id) : { compras: [], resumo: null };
  const mediaDe = (id) => historicoDe(id).resumo?.media ?? null;

  const aoAdicionar = (item) => {
    if (editando) { c.substituir(editando, item); setEditando(null); } else c.adicionar(item);
    setVerIng(item.ingredient_id);
  };
  const editar = (item) => { l.carregar(item); setEditando(item.id); setVerIng(item.ingredient_id); setAba("lancar"); };
  const voltar = () => navigate(ROTA_COMPRAS);
  const cancelar = () => (c.alterado ? setSair("sair") : voltar());
  const finalizar = async () => { if (await c.finalizar()) voltar(); };

  // Título e botão de voltar aparecem na hora, inclusive no carregamento.
  const titulo = (
    <div className="flex items-center gap-3 min-w-0">
      <button type="button" onClick={loteId ? cancelar : voltar} aria-label="Voltar para Compras"
        className="h-10 w-10 rounded-lg border flex items-center justify-center hover:bg-muted shrink-0"><ArrowLeft className="w-4 h-4" /></button>
      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0"><ShoppingCart className="w-5 h-5 text-primary" /></div>
      <div className="min-w-0">
        <h1 className="text-xl sm:text-2xl font-bold leading-tight">{loteId ? "Editar compra" : "Nova compra"}</h1>
        <p className="text-sm text-muted-foreground">Lance os itens como vieram na nota: por pacote, unidade ou quilo. O sistema faz as contas.</p>
      </div>
    </div>
  );

  if (c.carregando) {
    return (
      <div className="w-full max-w-[1600px] mx-auto px-4 sm:px-6 pt-4 sm:pt-5 space-y-4">
        {titulo}
        <NovaCompraCarregando />
      </div>
    );
  }

  return (
    // Altura cheia da área do painel: o rodapé fica preso embaixo e a rolagem
    // acontece dentro de cada coluna, não na página inteira.
    <div className="h-full flex flex-col overflow-hidden" data-e2e="nova-compra">
      <div className="shrink-0 w-full max-w-[1600px] mx-auto px-4 sm:px-6 pt-4 sm:pt-5 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          {titulo}
          {c.rascunhoRecuperado && !loteId && (
            <div className="flex items-center gap-2 rounded-lg border border-sky-500/30 bg-sky-500/10 px-3 py-2 text-sm" data-e2e="rascunho">
              <span>Continuamos de onde você parou.</span>
              <button type="button" onClick={() => setSair("zerar")} className="font-semibold text-sky-700 dark:text-sky-300 hover:underline">Começar do zero</button>
            </div>
          )}
        </div>

        <CabecalhoCompra c={c} />

        <div className="md:hidden grid grid-cols-3 gap-1 rounded-lg bg-muted/60 p-1">
          {ABAS.map(({ id, rotulo, icone: Icone }) => (
            <button key={id} type="button" onClick={() => setAba(id)}
              className={`h-10 rounded-md text-sm font-semibold flex items-center justify-center gap-1.5 ${aba === id ? "bg-background shadow-sm" : "text-muted-foreground"}`}>
              <Icone className="w-4 h-4" />{rotulo}{id === "itens" && c.itens.length > 0 && <span className="text-xs text-primary">{c.itens.length}</span>}
            </button>
          ))}
        </div>

      </div>

      {/* Adicionar item ocupa 3x a largura do histórico e dos itens */}
      <div className="flex-1 min-h-0 w-full max-w-[1600px] mx-auto px-4 sm:px-6 py-4">
        <div className="h-full grid gap-4 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] md:grid-rows-[auto_minmax(0,1fr)] xl:grid-cols-[minmax(0,1fr)_minmax(0,3fr)_minmax(0,1fr)] xl:grid-rows-1">
          <div className={`${aba === "historico" ? "" : "hidden"} md:block min-h-0 md:col-start-1 md:row-start-2 xl:row-start-1`}>
            <HistoricoIngrediente key={ingHistorico?.id || "vazio"} ing={ingHistorico} compras={hist.compras} resumo={hist.resumo} />
          </div>
          <div className={`${aba === "lancar" ? "" : "hidden"} md:flex flex-col min-h-0 md:col-start-1 md:row-start-1 xl:col-start-2`}>
            <LancarItem l={l} ingredientes={c.ingredientes} media={ingHistorico && l.ingId ? hist.resumo?.media : null}
              emEdicao={editando} aoAdicionar={aoAdicionar} aoEscolher={setVerIng}
              aoCancelarEdicao={() => { l.limpar(); setEditando(null); }} />
          </div>
          <div className={`${aba === "itens" ? "" : "hidden"} md:block min-h-0 md:col-start-2 md:row-start-1 md:row-span-2 xl:col-start-3 xl:row-span-1`}>
            <ItensDaCompra c={c} mediaDe={mediaDe} editando={editando}
              aoVer={(item) => setVerIng(item.ingredient_id)} aoEditar={editar} />
          </div>
        </div>
      </div>

      <RodapeCompra c={c} emEdicao={!!editando} aoCancelar={cancelar} aoFinalizar={finalizar} />

      <AlertDialog open={!!sair} onOpenChange={(v) => !v && setSair(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{loteId ? "Descartar as alterações?" : "Descartar esta compra?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {loteId ? "A compra continua como estava antes de você abrir." : `Os ${c.itens.length} itens lançados e o fornecedor serão apagados.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continuar lançando</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => { if (!loteId) c.descartar(); l.limpar(); setEditando(null); if (sair === "sair") voltar(); }}>
              Descartar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
